import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Play, Pause, Trash2, Edit2, X, ChevronRight, ChevronDown,
  TrendingUp, TrendingDown, Clock, AlertCircle, CheckCircle, Settings,
  BarChart3, Target, Shield, Zap, RefreshCw, Eye, DollarSign, Activity,
  Ban, Radio, Info, Filter, ArrowUpDown, Square, CheckSquare, FlaskConical, BookOpen
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import { formatPrice, formatDate, formatDateTime } from '../utils/formatters';
import { CONDITION_CATEGORIES, CONDITION_BY_NAME, POPULAR_STOCKS } from './strategy/strategyConstants';
import ConfirmModal from '../components/ConfirmModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function CustomSelect({ value, onChange, options, colors, style = {}, className = '' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleToggle = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(o => !o);
  };

  const selected = options.find(o => String(o.value) === String(value));

  return (
    <>
      <button ref={btnRef} type="button" onClick={handleToggle}
        className={`w-full flex items-center justify-between ${className}`}
        style={{ ...style, textAlign: 'left' }}>
        <span className="truncate text-[13px]">{selected?.label ?? '—'}</span>
        <span className="ml-2 flex-shrink-0 text-[10px]" style={{ color: colors.secondaryText }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && createPortal(
        <div ref={dropRef} style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: pos.width,
          zIndex: 99999,
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.accent}40`,
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px ${colors.accent}15`,
        }}>
          {options.map(opt => (
            <button key={opt.value} type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className="w-full px-3 py-2.5 text-left text-[13px] transition-all"
              style={{
                backgroundColor: String(opt.value) === String(value) ? `${colors.accent}25` : 'transparent',
                color: String(opt.value) === String(value) ? colors.accent : colors.primaryText,
              }}>
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

function AlgoFlowsPage() {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Strategy detail state
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [strategyTrades, setStrategyTrades] = useState([]);
  const [strategyLogs, setStrategyLogs] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [lastDecisions, setLastDecisions] = useState({});
  const [cooldowns, setCooldowns] = useState({});
  const [engineStatus, setEngineStatus] = useState(null);
  const [discipline, setDiscipline] = useState(null);
  const [showDisciplineInfo, setShowDisciplineInfo] = useState(false);
  const [report, setReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  // Backtest panel state
  const [showBacktestPanel, setShowBacktestPanel] = useState(false);
  const [backtestSymbol, setBacktestSymbol] = useState('');
  const [backtestStartDate, setBacktestStartDate] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().split('T')[0];
  });
  const [backtestEndDate, setBacktestEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [backtestCapital, setBacktestCapital] = useState(100000);
  const [backtestQty, setBacktestQty] = useState(10);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestResult, setBacktestResult] = useState(null);
  const [backtestError, setBacktestError] = useState('');

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(null); // strategyId to delete

  // List filter / sort / multi-select
  const [statusFilters, setStatusFilters] = useState([]); // [] = show all
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'pnl' | 'trades' | 'discipline'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // Create/Edit modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [availableStocks, setAvailableStocks] = useState(POPULAR_STOCKS);
  const [stockSearch, setStockSearch] = useState('');
  const [condSearch, setCondSearch] = useState('');
  const [dirFilter, setDirFilter] = useState('ALL'); // 'ALL' | 'bullish' | 'bearish'
  const [expandedCats, setExpandedCats] = useState({});
  const toggleCat = key => setExpandedCats(p => ({ ...p, [key]: !p[key] }));
  const [stockGridOpen, setStockGridOpen] = useState(false);
  const [watchlists, setWatchlists] = useState([]);

  const defaultFormData = {
    name: '',
    description: '',
    tags: [],
    capital_allocated: 100000,
    risk_per_trade: 1.0,
    max_trades_per_day: 3,
    cooldown_minutes: 1440,
    stocks: [],
    conditions: [],
    watchlist_id: '',
    // Engine config
    entry_logic: 'ALL',
    trend_filter: 'ANY',
    execution_mode: 'INSTANT',
    stop_loss_pct: 2.0,
    target_pct: 4.0,
    exit_type: 'RR',
    // Edge Report gate
    edge_gate_enabled: false,
    edge_gate_min_confidence: 60,
    edge_gate_decisions: ['SWING BUY', 'BUY (MODERATE)', 'INVEST'],
  };
  const [formData, setFormData] = useState(defaultFormData);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/algo-flows/dashboard`),
        axios.get(`${API_URL}/engine/status`).catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      setEngineStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Fetch strategy details
  const fetchStrategyDetails = async (strategyId) => {
    try {
      const [perfRes, tradesRes, logsRes, decisionsRes, cooldownsRes, disciplineRes] = await Promise.all([
        axios.get(`${API_URL}/algo-flows/strategies/${strategyId}/performance`),
        axios.get(`${API_URL}/algo-flows/strategies/${strategyId}/trades`),
        axios.get(`${API_URL}/algo-flows/strategies/${strategyId}/logs`),
        axios.get(`${API_URL}/algo-flows/strategies/${strategyId}/last-decisions`).catch(() => ({ data: { decisions: {} } })),
        axios.get(`${API_URL}/algo-flows/strategies/${strategyId}/next-eligible`).catch(() => ({ data: { eligibility: {} } })),
        axios.get(`${API_URL}/algo-flows/strategies/${strategyId}/discipline`).catch(() => ({ data: null })),
      ]);
      setPerformance(perfRes.data);
      setStrategyTrades(tradesRes.data.trades || []);
      setStrategyLogs(logsRes.data.logs || []);
      setLastDecisions(decisionsRes.data.decisions || {});
      setCooldowns(cooldownsRes.data.eligibility || {});
      setDiscipline(disciplineRes.data);
    } catch (error) {
      console.error('Error fetching strategy details:', error);
      toast.error("Couldn't load strategy details.");
    }
  };

  // Select a strategy
  const handleSelectStrategy = (strategy) => {
    setSelectedStrategy(strategy);
    fetchStrategyDetails(strategy.id);
  };

  // Fetch and show report card
  const handleShowReport = async (strategyId) => {
    setReportLoading(true);
    setShowReport(true);
    try {
      const res = await axios.get(`${API_URL}/algo-flows/strategies/${strategyId}/report`);
      setReport(res.data);
    } catch (e) {
      toast.error('Failed to load report');
      setShowReport(false);
    } finally {
      setReportLoading(false);
    }
  };

  // Run backtest from strategy
  const handleRunStrategyBacktest = async (strategyId) => {
    if (!backtestSymbol) { setBacktestError('Select a stock symbol'); return; }
    setBacktestError('');
    setBacktestLoading(true);
    setBacktestResult(null);
    try {
      const res = await axios.post(`${API_URL}/backtest/run-from-strategy/${strategyId}`, {
        symbol: backtestSymbol,
        start_date: backtestStartDate,
        end_date: backtestEndDate,
        capital: parseFloat(backtestCapital),
        quantity: parseInt(backtestQty),
      });
      setBacktestResult(res.data);
    } catch (e) {
      setBacktestError(e.response?.data?.detail || 'Backtest failed');
    } finally {
      setBacktestLoading(false);
    }
  };

  // Create strategy
  const handleCreateStrategy = async () => {
    if (!formData.name.trim()) {
      toast.error('Strategy name is required');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/algo-flows/strategies`, formData);
      if (response.data.success) {
        toast.success('Strategy created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchDashboard();
      }
    } catch (error) {
      console.error('Error creating strategy:', error);
      toast.error('Failed to create strategy');
    }
  };

  // Update strategy
  const handleUpdateStrategy = async () => {
    if (!editingStrategy) return;
    
    try {
      const response = await axios.put(
        `${API_URL}/algo-flows/strategies/${editingStrategy.id}`,
        formData
      );
      if (response.data.success) {
        toast.success('Strategy updated!');
        setShowCreateModal(false);
        setEditingStrategy(null);
        resetForm();
        fetchDashboard();
        if (selectedStrategy?.id === editingStrategy.id) {
          setSelectedStrategy(response.data.strategy);
          fetchStrategyDetails(editingStrategy.id);
        }
      }
    } catch (error) {
      console.error('Error updating strategy:', error);
      toast.error('Failed to update strategy');
    }
  };

  // Pause/Resume strategy
  const handleToggleStatus = async (strategy) => {
    const action = strategy.status === 'ACTIVE' ? 'pause' : 'resume';
    try {
      await axios.post(`${API_URL}/algo-flows/strategies/${strategy.id}/${action}`);
      toast.success(`Strategy ${action}d`);
      fetchDashboard();
      if (selectedStrategy?.id === strategy.id) {
        fetchStrategyDetails(strategy.id);
      }
    } catch (error) {
      toast.error(`Failed to ${action} strategy`);
    }
  };

  // Delete strategy
  const handleDeleteStrategy = (strategyId) => {
    setConfirmDelete(strategyId);
  };

  const confirmDeleteStrategy = async () => {
    const strategyId = confirmDelete;
    setConfirmDelete(null);
    try {
      await axios.delete(`${API_URL}/algo-flows/strategies/${strategyId}`);
      toast.success('Strategy deleted');
      fetchDashboard();
      if (selectedStrategy?.id === strategyId) {
        setSelectedStrategy(null);
      }
    } catch (error) {
      toast.error('Failed to delete strategy');
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmBulkDelete(true);
  };

  const confirmBulkDeleteStrategies = async () => {
    setConfirmBulkDelete(false);
    const ids = [...selectedIds];
    try {
      await axios.delete(`${API_URL}/algo-flows/strategies`, { data: { strategy_ids: ids } });
      toast.success(`${ids.length} ${ids.length === 1 ? 'strategy' : 'strategies'} deleted`);
      setSelectedIds(new Set());
      if (ids.includes(selectedStrategy?.id)) setSelectedStrategy(null);
      fetchDashboard();
    } catch (error) {
      toast.error('Bulk delete failed');
    }
  };

  const toggleSelectId = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (visibleStrategies) => {
    if (selectedIds.size === visibleStrategies.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleStrategies.map(s => s.id)));
    }
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  // Open edit modal
  const openEditModal = (strategy) => {
    setEditingStrategy(strategy);
    const cfg = strategy.config || {};
    setFormData({
      name: strategy.name,
      description: strategy.description || '',
      tags: strategy.tags || [],
      capital_allocated: strategy.capital_allocated,
      risk_per_trade: strategy.risk_per_trade,
      max_trades_per_day: strategy.max_trades_per_day,
      cooldown_minutes: strategy.cooldown_minutes,
      stocks: strategy.stocks || [],
      conditions: strategy.conditions || [],
      entry_logic:    cfg.entry_logic    || 'ALL',
      trend_filter:   cfg.trend_filter   || 'ANY',
      execution_mode: cfg.execution_mode || 'INSTANT',
      stop_loss_pct:  cfg.stop_loss_pct  ?? 2.0,
      target_pct:     cfg.target_pct     ?? 4.0,
      exit_type:      cfg.exit_type      || 'RR',
      edge_gate_enabled:          cfg.edge_gate_enabled          ?? false,
      edge_gate_min_confidence:   cfg.edge_gate_min_confidence   ?? 60,
      edge_gate_decisions:        cfg.edge_gate_decisions        ?? ['SWING BUY', 'BUY (MODERATE)', 'INVEST'],
      watchlist_id: strategy.watchlist_id ? String(strategy.watchlist_id) : '',
    });
    setStockSearch('');
    setCondSearch('');
    setShowCreateModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData(defaultFormData);
    setStockSearch('');
    setCondSearch('');
    setEditingStrategy(null);
  };

  // Toggle stock selection
  const toggleStock = (symbol) => {
    setFormData(prev => ({
      ...prev,
      stocks: prev.stocks.includes(symbol)
        ? prev.stocks.filter(s => s !== symbol)
        : [...prev.stocks, symbol]
    }));
  };

  // Toggle condition selection — adds with default params
  const toggleCondition = (condDef) => {
    setFormData(prev => {
      const exists = prev.conditions.some(c => c.name === condDef.name);
      return {
        ...prev,
        conditions: exists
          ? prev.conditions.filter(c => c.name !== condDef.name)
          : [...prev.conditions, {
              name: condDef.name,
              type: condDef.type || 'scanner',
              params: Object.fromEntries(
                (condDef.params || []).map(p => [p.key, p.default])
              ),
            }],
      };
    });
  };

  // Update a param value for an already-selected condition
  const updateConditionParam = (condName, paramKey, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map(c =>
        c.name === condName
          ? { ...c, params: { ...(c.params || {}), [paramKey]: value } }
          : c
      ),
    }));
  };

  // Fetch available stocks + watchlists from API on modal open
  useEffect(() => {
    if (!showCreateModal) return;
    axios.get(`${API_URL}/stocks`)
      .then(res => {
        const syms = (res.data || []).map(s => s.symbol).filter(Boolean);
        if (syms.length > 0) setAvailableStocks(syms);
      })
      .catch(() => {});
    axios.get(`${API_URL}/watchlist`)
      .then(res => setWatchlists(res.data || []))
      .catch(() => {});
  }, [showCreateModal]);

  const formatTime = (isoString) => formatDateTime(isoString);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.accent }}></div>
      </div>
    );
  }


  return (
    <div className="space-y-4 pb-0.5.5" data-testid="strategy-lab-page">
      {confirmDelete && (
        <ConfirmModal
          message="Are you sure you want to delete this strategy? This cannot be undone."
          confirmLabel="Delete"
          onConfirm={confirmDeleteStrategy}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {confirmBulkDelete && (
        <ConfirmModal
          message={`Delete ${selectedIds.size} selected ${selectedIds.size === 1 ? 'strategy' : 'strategies'}? This cannot be undone.`}
          confirmLabel={`Delete ${selectedIds.size}`}
          onConfirm={confirmBulkDeleteStrategies}
          onCancel={() => setConfirmBulkDelete(false)}
        />
      )}

      {/* Templates modal */}
      {showTemplates && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4">
          <div className="w-full sm:max-w-md max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl"
            style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.accent}35`, boxShadow: `0 -24px 60px rgba(0,0,0,0.7)` }}>
            <div className="h-1 rounded-t-3xl sm:rounded-t-2xl" style={{ background: `linear-gradient(90deg, ${colors.accent}, #8b5cf6, #06b6d4)` }} />
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4" style={{ color: colors.accent }} />
                  <h2 className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Starter Templates</h2>
                </div>
                <button onClick={() => setShowTemplates(false)} className="p-1 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                  <X className="w-4 h-4" style={{ color: colors.secondaryText }} />
                </button>
              </div>
              <p className="text-[12px]" style={{ color: colors.secondaryText }}>Pick a template to pre-fill the strategy builder. You can customise everything before saving.</p>

              {[
                {
                  name: 'RSI Bounce',
                  description: 'Buy oversold stocks showing RSI reversal',
                  icon: '◆',
                  color: colors.profit,
                  conditions: [
                    { name: 'RSI_OVERSOLD', type: 'direct', params: { threshold: 30 } },
                    { name: 'PRICE_ABOVE_SMA50', type: 'direct', params: {} },
                  ],
                  stop_loss_pct: 2.0, target_pct: 4.0,
                  stocks: ['RELIANCE.NS', 'TCS.NS', 'INFY.NS', 'HDFCBANK.NS', 'ICICIBANK.NS'],
                },
                {
                  name: 'EMA Crossover',
                  description: 'Enter when MACD confirms trend direction',
                  icon: '▲',
                  color: colors.accent,
                  conditions: [
                    { name: 'MACD_BULLISH_CROSSOVER', type: 'direct', params: {} },
                    { name: 'PRICE_ABOVE_SMA20', type: 'direct', params: {} },
                    { name: 'ADX_STRONG_TREND', type: 'direct', params: { min_adx: 25 } },
                  ],
                  stop_loss_pct: 2.5, target_pct: 5.0,
                  stocks: ['SBIN.NS', 'AXISBANK.NS', 'KOTAKBANK.NS', 'BAJFINANCE.NS', 'LT.NS'],
                },
                {
                  name: 'Breakout + Volume',
                  description: 'Trade breakouts confirmed by high volume',
                  icon: '⚡',
                  color: '#f59e0b',
                  conditions: [
                    { name: 'BREAKOUT_VOLUME_SPIKE', type: 'scanner', params: {} },
                    { name: 'PRICE_ABOVE_SMA20', type: 'direct', params: {} },
                  ],
                  stop_loss_pct: 3.0, target_pct: 6.0,
                  stocks: ['TATASTEEL.NS', 'HINDALCO.NS', 'MARUTI.NS', 'BHARTIARTL.NS', 'ITC.NS'],
                },
              ].map(tpl => (
                <button
                  key={tpl.name}
                  onClick={() => {
                    resetForm();
                    setFormData(f => ({
                      ...f,
                      name: tpl.name,
                      description: tpl.description,
                      conditions: tpl.conditions,
                      stop_loss_pct: tpl.stop_loss_pct,
                      target_pct: tpl.target_pct,
                      stocks: tpl.stocks,
                    }));
                    setShowTemplates(false);
                    setShowCreateModal(true);
                  }}
                  className="w-full text-left rounded-xl p-3 transition-all"
                  style={{ background: `linear-gradient(135deg, ${tpl.color}12 0%, ${tpl.color}04 100%)`, border: `1px solid ${tpl.color}35` }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-bold" style={{ color: tpl.color }}>{tpl.icon}</span>
                      <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{tpl.name}</span>
                    </div>
                    <span className="text-[12px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${tpl.color}20`, color: tpl.color }}>Use →</span>
                  </div>
                  <p className="text-[12px] ml-6 mb-2" style={{ color: colors.secondaryText }}>{tpl.description}</p>
                  <div className="flex flex-wrap gap-1 ml-6">
                    {tpl.conditions.map(c => (
                      <span key={c.name} className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${tpl.color}18`, color: tpl.color }}>
                        {CONDITION_BY_NAME[c.name]?.display || c.name}
                      </span>
                    ))}
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
                      SL {tpl.stop_loss_pct}% · TP {tpl.target_pct}%
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>Algo Flows</h1>
          <p className="text-[13px]" style={{ color: colors.secondaryText }}>Create and manage automated paper trading strategies</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-semibold text-white whitespace-nowrap flex-shrink-0 transition-all"
          style={{ background: `linear-gradient(135deg, ${colors.accent}, #8b5cf6)`, boxShadow: `0 2px 12px ${colors.accent}40` }}
          data-testid="create-strategy-btn"
          data-tour="algo-create-btn"
        >
          <Plus className="w-4 h-4" />
          <span>New Strategy</span>
        </button>
      </div>

      {/* Learning nudge */}
      <button
        onClick={() => navigate('/learning?view=level&level=13&card=0')}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
        style={{ background: 'linear-gradient(135deg, #4ecca312 0%, #4ecca304 100%)', border: '1px solid #4ecca328' }}>
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#4ecca3' }} />
          <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Building your first strategy? <span style={{ color: '#4ecca3' }}>Learn to Automate Your Edge</span>
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#4ecca350' }} />
      </button>

      {/* Templates nudge */}
      <button
        onClick={() => setShowTemplates(true)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
        style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.accent}28` }}>
        <div className="flex items-center gap-2">
          <FlaskConical className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.accent }} />
          <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Not sure how to build? <span style={{ color: colors.accent }}>View our starter templates</span>
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: `${colors.accent}50` }} />
      </button>

      {/* Dashboard Summary */}
      <div data-tour="algo-dashboard" className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="app-card rounded-2xl p-3" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
          <div className="flex items-center space-x-1.5 mb-1.5">
            <Target className="w-3.5 h-3.5" style={{ color: colors.accent }} />
            <span className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>Active Strategies</span>
          </div>
          <p className="text-[20px] font-bold" style={{ color: colors.primaryText }}>
            {dashboard?.active_strategies || 0}
          </p>
        </div>
        <div className="app-card rounded-2xl p-3" style={{ background: 'linear-gradient(135deg, #f59e0b18 0%, #f59e0b06 100%)', border: '1px solid #f59e0b35' }}>
          <div className="flex items-center space-x-1.5 mb-1.5">
            <DollarSign className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
            <span className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>Capital Deployed</span>
          </div>
          <p className="text-[20px] font-bold" style={{ color: colors.primaryText }}>
            {formatPrice(dashboard?.total_capital_allocated || 0)}
          </p>
        </div>
        {(() => {
          const pnl = dashboard?.total_pnl || 0;
          const pnlColor = pnl >= 0 ? '#16C784' : '#ef4444';
          return (
            <div className="app-card rounded-2xl p-3" style={{ background: `linear-gradient(135deg, ${pnlColor}18 0%, ${pnlColor}06 100%)`, border: `1px solid ${pnlColor}35` }}>
              <div className="flex items-center space-x-1.5 mb-1.5">
                <BarChart3 className="w-3.5 h-3.5" style={{ color: pnlColor }} />
                <span className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>Total P&L</span>
              </div>
              <p className="text-[20px] font-bold" style={{ color: pnlColor }}>
                {pnl >= 0 ? '+' : ''}{formatPrice(pnl)}
              </p>
            </div>
          );
        })()}
        <div className="app-card rounded-2xl p-3" style={{ background: 'linear-gradient(135deg, #06b6d418 0%, #06b6d406 100%)', border: '1px solid #06b6d435' }}>
          <div className="flex items-center space-x-1.5 mb-1.5">
            <Zap className="w-3.5 h-3.5" style={{ color: '#06b6d4' }} />
            <span className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>Total Trades</span>
          </div>
          <p className="text-[20px] font-bold" style={{ color: colors.primaryText }}>
            {dashboard?.total_trades || 0}
          </p>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Strategies List */}
        {(() => {
          const allStrategies = dashboard?.strategies || [];
          const visibleStrategies = allStrategies
            .filter(s => statusFilters.length === 0 || statusFilters.includes(s.status))
            .sort((a, b) => {
              if (sortBy === 'pnl') return (b.total_pnl || 0) - (a.total_pnl || 0);
              if (sortBy === 'trades') return (b.total_trades || 0) - (a.total_trades || 0);
              if (sortBy === 'discipline') return (b.discipline_score || 0) - (a.discipline_score || 0);
              return a.name.localeCompare(b.name);
            });
          const allVisibleSelected = visibleStrategies.length > 0 && visibleStrategies.every(s => selectedIds.has(s.id));

          return (
        <div data-tour="algo-strategies-list" className="app-card lg:col-span-1 rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
          {/* List header */}
          <div className="p-2 border-b space-y-2" style={{ borderColor: colors.border }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleSelectAll(visibleStrategies)}
                  className="p-0.5 rounded"
                  title={allVisibleSelected ? 'Deselect all' : 'Select all'}
                  style={{ color: colors.secondaryText }}
                >
                  {allVisibleSelected
                    ? <CheckSquare className="w-4 h-4" style={{ color: colors.accent }} />
                    : <Square className="w-4 h-4" />}
                </button>
                <h2 className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>Your Strategies</h2>
              </div>
              <div className="flex items-center gap-1">
                {selectedIds.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-medium bg-red-500/20 text-red-400"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete ({selectedIds.size})
                  </button>
                )}
              </div>
            </div>

            {/* Status filter chips */}
            <div className="flex items-center gap-1 flex-wrap">
              <Filter className="w-3 h-3 shrink-0" style={{ color: colors.secondaryText }} />
              {['ACTIVE', 'PAUSED', 'DELETED'].map(s => (
                <button
                  key={s}
                  onClick={() => toggleStatusFilter(s)}
                  className={`text-[11px] px-1.5 py-0.5 rounded-full border transition-colors ${
                    statusFilters.includes(s) ? '' : 'opacity-50'
                  }`}
                  style={{
                    borderColor: s === 'ACTIVE' ? '#22c55e' : s === 'PAUSED' ? '#eab308' : '#ef4444',
                    backgroundColor: statusFilters.includes(s)
                      ? s === 'ACTIVE' ? 'rgba(34,197,94,0.15)' : s === 'PAUSED' ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.15)'
                      : 'transparent',
                    color: s === 'ACTIVE' ? '#4ade80' : s === 'PAUSED' ? '#facc15' : '#f87171',
                  }}
                >
                  {s}
                </button>
              ))}
              {statusFilters.length > 0 && (
                <button onClick={() => setStatusFilters([])} className="text-[11px] px-1 py-0.5 rounded" style={{ color: colors.secondaryText }}>
                  Clear
                </button>
              )}
            </div>

            <div className="flex items-center gap-1">
              <ArrowUpDown className="w-3 h-3 shrink-0" style={{ color: colors.secondaryText }} />
              {[['name', 'Name'], ['pnl', 'P&L'], ['trades', 'Trades'], ['discipline', 'Discipline']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setSortBy(val)}
                  className={`text-[11px] px-1.5 py-0.5 rounded transition-colors`}
                  style={{
                    backgroundColor: sortBy === val ? `${colors.accent}25` : 'transparent',
                    color: sortBy === val ? colors.accent : colors.secondaryText,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="divide-y" style={{ borderColor: colors.border }}>
            {allStrategies.length === 0 ? (
              <div className="p-4 text-center">
                <Settings className="w-10 h-10 mx-auto mb-2" style={{ color: colors.secondaryText }} />
                <p className="text-[14px]" style={{ color: colors.secondaryText }}>No strategies yet</p>
                <button onClick={() => setShowTemplates(true)} className="text-[13px] mt-1" style={{ color: colors.accent }}>
                  Browse templates →
                </button>
              </div>
            ) : visibleStrategies.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-[13px]" style={{ color: colors.secondaryText }}>No strategies match the selected filters</p>
              </div>
            ) : (
              visibleStrategies.map((strategy) => (
                <div
                  key={strategy.id}
                  className={`p-2 cursor-pointer transition-colors ${selectedStrategy?.id === strategy.id ? 'ring-1 ring-inset' : ''}`}
                  style={{
                    backgroundColor: selectedStrategy?.id === strategy.id ? colors.inputBg : 'transparent',
                  }}
                  onClick={() => handleSelectStrategy(strategy)}
                  data-testid={`strategy-item-${strategy.id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        onClick={(e) => toggleSelectId(strategy.id, e)}
                        className="shrink-0 p-0.5 rounded"
                        style={{ color: selectedIds.has(strategy.id) ? colors.accent : colors.secondaryText }}
                      >
                        {selectedIds.has(strategy.id)
                          ? <CheckSquare className="w-3.5 h-3.5" />
                          : <Square className="w-3.5 h-3.5" />}
                      </button>
                      <span className="text-[13px] font-medium truncate" style={{ color: colors.primaryText }}>
                        {strategy.name}
                      </span>
                      <span
                        className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded-full ${
                          strategy.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' :
                          strategy.status === 'PAUSED' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}
                      >
                        {strategy.status}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: colors.secondaryText }} />
                  </div>
                  <div className="flex items-center justify-between text-[12px] pl-6">
                    <span style={{ color: colors.secondaryText }}>
                      {formatPrice(strategy.capital_allocated)}
                    </span>
                    <span style={{ color: strategy.total_pnl >= 0 ? colors.profit : colors.loss }}>
                      {strategy.total_pnl >= 0 ? '+' : ''}{formatPrice(strategy.total_pnl)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[12px] pl-6" style={{ color: colors.secondaryText }}>
                    <span className="flex items-center gap-2">
                      <span>{strategy.total_trades} trades</span>
                      <span>·</span>
                      <span>{strategy.stocks?.length || 0} stocks</span>
                      {strategy.total_trades > 0 && (() => {
                        const wr = strategy.winning_trades != null
                          ? Math.round(strategy.winning_trades / strategy.total_trades * 100)
                          : null;
                        if (wr == null) return null;
                        return (
                          <>
                            <span>·</span>
                            <span style={{ color: wr >= 55 ? '#16C784' : wr >= 40 ? '#f59e0b' : '#ef4444' }}>
                              {wr}% WR
                            </span>
                          </>
                        );
                      })()}
                    </span>
                    {strategy.discipline_score != null && (
                      <span className="text-[12px] px-1.5 py-0.5 rounded" style={{
                        backgroundColor: strategy.discipline_score >= 85 ? 'rgba(34,197,94,0.1)' : strategy.discipline_score >= 70 ? 'rgba(59,130,246,0.1)' : strategy.discipline_score >= 50 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                        color: strategy.discipline_score >= 85 ? '#16C784' : strategy.discipline_score >= 70 ? '#6366F1' : strategy.discipline_score >= 50 ? '#f59e0b' : '#ef4444',
                      }}>
                        D: {strategy.discipline_score}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
          );
        })()}

        {/* Strategy Details */}
        <div data-tour="algo-strategy-detail" className="lg:col-span-2 space-y-4">
          {selectedStrategy ? (
            <>
              {/* Strategy Header */}
              <div className="app-card rounded-2xl p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="min-w-0">
                    <h2 className="text-[14px] font-bold truncate" style={{ color: colors.primaryText }}>{selectedStrategy.name}</h2>
                    <p className="text-[13px] truncate" style={{ color: colors.secondaryText }}>{selectedStrategy.description || 'No description'}</p>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <button
                      onClick={() => handleShowReport(selectedStrategy.id)}
                      className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-lg text-[13px] font-medium"
                      style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}
                      title="View Report Card"
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                      Report
                    </button>
                    <button
                      onClick={() => { setShowBacktestPanel(p => !p); setBacktestResult(null); setBacktestError(''); }}
                      className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-lg text-[13px] font-medium"
                      style={{ backgroundColor: showBacktestPanel ? `${colors.accent}40` : `${colors.accent}20`, color: colors.accent }}
                      title="Backtest this strategy"
                    >
                      <Activity className="w-3.5 h-3.5" />
                      Backtest
                    </button>
                    <button
                      onClick={() => handleToggleStatus(selectedStrategy)}
                      className="p-1 rounded-lg"
                      style={{ backgroundColor: selectedStrategy.status === 'ACTIVE' ? 'rgba(234, 179, 8, 0.2)' : 'rgba(34, 197, 94, 0.2)' }}
                      data-testid="toggle-status-btn"
                    >
                      {selectedStrategy.status === 'ACTIVE' ? (
                        <Pause className="w-4 h-4 text-yellow-500" />
                      ) : (
                        <Play className="w-4 h-4 text-green-500" />
                      )}
                    </button>
                    <button
                      onClick={() => openEditModal(selectedStrategy)}
                      className="p-1 rounded-lg"
                      style={{ backgroundColor: `${colors.accent}20` }}
                      data-testid="edit-strategy-btn"
                    >
                      <Edit2 className="w-4 h-4" style={{ color: colors.accent }} />
                    </button>
                    <button
                      onClick={() => handleDeleteStrategy(selectedStrategy.id)}
                      className="p-1 rounded-lg"
                      style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
                      data-testid="delete-strategy-btn"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Performance Metrics */}
                {performance && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                      <p className="text-[13px]" style={{ color: colors.secondaryText }}>Win Rate</p>
                      <p className="text-[14px] font-bold" style={{ color: colors.profit }}>{performance.win_rate}%</p>
                    </div>
                    <div className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                      <p className="text-[13px]" style={{ color: colors.secondaryText }}>ROI</p>
                      <p className="text-[14px] font-bold" style={{ color: performance.roi_pct >= 0 ? colors.profit : colors.loss }}>
                        {performance.roi_pct >= 0 ? '+' : ''}{performance.roi_pct}%
                      </p>
                    </div>
                    <div className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                      <p className="text-[13px]" style={{ color: colors.secondaryText }}>W / L</p>
                      <p className="text-[14px] font-bold" style={{ color: colors.primaryText }}>
                        <span className="text-green-400">{performance.winning_trades}</span>
                        {' / '}
                        <span className="text-red-400">{performance.losing_trades}</span>
                      </p>
                    </div>
                    <div className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                      <p className="text-[13px]" style={{ color: colors.secondaryText }}>Open Trades</p>
                      <p className="text-[14px] font-bold" style={{ color: colors.primaryText }}>{performance.open_trades}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Backtest Panel */}
              {showBacktestPanel && (() => {
                const cs2 = { background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` };
                const inp = { backgroundColor: colors.inputBg, color: colors.primaryText, borderWidth: '1px', borderColor: colors.border, outline: 'none' };
                const pct = v => v != null ? `${v >= 0 ? '+' : ''}${parseFloat(v).toFixed(2)}%` : '—';
                const stratStocks = selectedStrategy.stocks || [];
                return (
                  <div data-tour="algo-backtest-panel" className="app-card rounded-2xl p-3 space-y-3" style={cs2}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>Backtest: {selectedStrategy.name}</h3>
                        <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                          {(selectedStrategy.conditions || []).length} conditions · {selectedStrategy.config?.entry_logic || 'ALL'} logic · SL {selectedStrategy.config?.stop_loss_pct ?? 5}% · TP {selectedStrategy.config?.target_pct ?? 10}%
                        </p>
                      </div>
                      <button onClick={() => setShowBacktestPanel(false)} className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                        <X className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <label className="text-[13px]" style={{ color: colors.secondaryText }}>Symbol</label>
                        <select className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inp}
                          value={backtestSymbol} onChange={e => setBacktestSymbol(e.target.value)}>
                          <option value="">— Select —</option>
                          {stratStocks.length > 0 && (
                            <optgroup label="Strategy stocks">
                              {stratStocks.map(s => <option key={s} value={s}>{s.replace('.NS', '')}</option>)}
                            </optgroup>
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="text-[13px]" style={{ color: colors.secondaryText }}>Start Date</label>
                        <input type="date" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inp}
                          value={backtestStartDate} onChange={e => setBacktestStartDate(e.target.value)} />
                      </div>
                      <div>
                        <label className="text-[13px]" style={{ color: colors.secondaryText }}>End Date</label>
                        <input type="date" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inp}
                          value={backtestEndDate} onChange={e => setBacktestEndDate(e.target.value)} />
                      </div>
                      <div>
                        <label className="text-[13px]" style={{ color: colors.secondaryText }}>Capital (₹)</label>
                        <input type="number" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inp}
                          value={backtestCapital} onChange={e => setBacktestCapital(e.target.value)} min={10000} step={10000} />
                      </div>
                      <div>
                        <label className="text-[13px]" style={{ color: colors.secondaryText }}>Qty per trade</label>
                        <input type="number" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inp}
                          value={backtestQty} onChange={e => setBacktestQty(e.target.value)} min={1} step={1} />
                      </div>
                    </div>

                    {backtestError && <p className="text-[13px] text-red-400">{backtestError}</p>}

                    <button
                      onClick={() => handleRunStrategyBacktest(selectedStrategy.id)}
                      disabled={backtestLoading}
                      className="w-full py-1.5 rounded-lg text-[13px] font-semibold text-white"
                      style={{ backgroundColor: backtestLoading ? colors.secondaryText : colors.accent }}>
                      {backtestLoading ? 'Running…' : 'Run Backtest'}
                    </button>

                    {backtestResult && (
                      <div className="rounded-lg p-1.5 space-y-2" style={{ backgroundColor: colors.inputBg }}>
                        <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Results — {backtestResult.symbol}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[13px]">
                          {[
                            ['Return', pct(backtestResult.total_return_pct), backtestResult.total_return_pct >= 0 ? colors.profit : colors.loss],
                            ['Win Rate', pct(backtestResult.win_rate_pct), colors.primaryText],
                            ['Trades', backtestResult.num_trades, colors.primaryText],
                            ['Max DD', pct(backtestResult.max_drawdown_pct), colors.loss],
                          ].map(([label, val, color]) => (
                            <div key={label} className="rounded p-1" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
                              <p style={{ color: colors.secondaryText }}>{label}</p>
                              <p className="font-bold mt-0.5" style={{ color }}>{val}</p>
                            </div>
                          ))}
                        </div>
                        <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                          Final capital: ₹{backtestResult.capital_final?.toLocaleString('en-IN')} · Sharpe: {backtestResult.sharpe_ratio?.toFixed(2) ?? '—'}
                        </p>
                        {backtestResult.id && (
                          <button
                            onClick={() => navigate('/profile?section=backtest', { state: { loadResultId: backtestResult.id } })}
                            className="w-full py-1 rounded text-[13px] font-medium mt-1"
                            style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                            View Full Results →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Engine Status Panel */}
              <div className="app-card rounded-2xl p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
                <div className="flex items-center space-x-2 mb-3">
                  <Radio className="w-4 h-4" style={{ color: engineStatus?.is_healthy ? '#16C784' : colors.secondaryText }} />
                  <h3 className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>Engine Status</h3>
                  <span className={`text-[13px] px-1 py-0.5 rounded-full font-medium ${
                    engineStatus?.is_healthy ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {engineStatus?.is_healthy ? 'Healthy' : engineStatus ? 'Stale' : 'No data'}
                  </span>
                  {engineStatus?.cycle_time_ms && (
                    <span className="text-[13px]" style={{ color: colors.secondaryText }}>{engineStatus.cycle_time_ms}ms</span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px]">
                  <div className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                    <p style={{ color: colors.secondaryText }}>Last Evaluation</p>
                    <p className="font-medium mt-0.5" style={{ color: colors.primaryText }}>
                      {engineStatus?.last_heartbeat ? formatTime(engineStatus.last_heartbeat) : 'Not yet'}
                    </p>
                  </div>
                  <div className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                    <p style={{ color: colors.secondaryText }}>Evaluation Cycle</p>
                    <p className="font-medium mt-0.5" style={{ color: colors.primaryText }}>Every 1 min (market hours)</p>
                  </div>
                  <div className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                    <p style={{ color: colors.secondaryText }}>Symbols Monitored</p>
                    <p className="font-medium mt-0.5" style={{ color: colors.primaryText }}>{selectedStrategy.stocks?.length || 0}</p>
                  </div>
                  <div className="p-1 rounded" style={{ backgroundColor: colors.inputBg }}>
                    <p style={{ color: colors.secondaryText }}>Today's Decisions</p>
                    <p className="font-medium mt-0.5" style={{ color: colors.primaryText }}>
                      {strategyLogs.filter(l => {
                        const d = new Date(l.execution_time);
                        const today = new Date();
                        return d.toDateString() === today.toDateString();
                      }).length}
                    </p>
                  </div>
                </div>
              </div>

              {/* Strategy Configuration */}
              <div className="app-card rounded-2xl p-3" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
                <h3 className="text-[14px] font-semibold mb-3" style={{ color: colors.primaryText }}>Configuration</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px]">
                  <div>
                    <span style={{ color: colors.secondaryText }}>Capital</span>
                    <p className="font-medium" style={{ color: colors.primaryText }}>{formatPrice(selectedStrategy.capital_allocated)}</p>
                  </div>
                  <div>
                    <span style={{ color: colors.secondaryText }}>Risk/Trade</span>
                    <p className="font-medium" style={{ color: colors.primaryText }}>{selectedStrategy.risk_per_trade}%</p>
                  </div>
                  <div>
                    <span style={{ color: colors.secondaryText }}>Max Trades/Day</span>
                    <p className="font-medium" style={{ color: colors.primaryText }}>{selectedStrategy.max_trades_per_day}</p>
                  </div>
                  <div>
                    <span style={{ color: colors.secondaryText }}>Cooldown</span>
                    <p className="font-medium" style={{ color: colors.primaryText }}>
                      {selectedStrategy.cooldown_minutes >= 1440 
                        ? `${Math.floor(selectedStrategy.cooldown_minutes / 1440)} day(s)` 
                        : `${selectedStrategy.cooldown_minutes} min`}
                    </p>
                  </div>
                </div>
                
                {/* Stocks */}
                <div className="mt-3">
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>Stocks ({selectedStrategy.stocks?.length || 0})</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(selectedStrategy.stocks || []).map(symbol => (
                      <span 
                        key={symbol} 
                        className="px-1 py-0.5 rounded text-[13px]"
                        style={{ backgroundColor: colors.inputBg, color: colors.primaryText }}
                      >
                        {symbol.replace('.NS', '')}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Conditions */}
                <div className="mt-3">
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>Conditions ({selectedStrategy.conditions?.length || 0})</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(selectedStrategy.conditions || []).map(cond => (
                      <span
                        key={cond.name}
                        className="px-1 py-0.5 rounded text-[13px]"
                        style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}
                      >
                        {cond.display || cond.name}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Engine Rules */}
                {selectedStrategy.config && (
                  <div className="mt-3 pt-0.5 border-t" style={{ borderColor: colors.border }}>
                    <span className="text-[13px] font-medium" style={{ color: colors.primaryText }}>Engine Rules</span>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mt-2 text-[13px]">
                      <div className="flex justify-between">
                        <span style={{ color: colors.secondaryText }}>Match Logic</span>
                        <span className="font-medium" style={{ color: colors.primaryText }}>
                          {selectedStrategy.config.entry_logic === 'ALL' ? 'ALL conditions' : 'ANY condition'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: colors.secondaryText }}>Entry Mode</span>
                        <span className="font-medium" style={{ color: colors.primaryText }}>
                          {selectedStrategy.config.execution_mode === 'INSTANT' ? 'Instant (live price)' : 'Candle Close'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: colors.secondaryText }}>Trend Filter</span>
                        <span className="font-medium" style={{ color: colors.primaryText }}>
                          {selectedStrategy.config.trend_filter === 'ANY' ? 'Any direction' : selectedStrategy.config.trend_filter}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: colors.secondaryText }}>Exit Type</span>
                        <span className="font-medium" style={{ color: colors.primaryText }}>
                          {selectedStrategy.config.exit_type === 'RR' ? 'SL / Target hit' : 'Time-based'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: colors.secondaryText }}>Stop Loss</span>
                        <span className="font-medium text-red-400">{selectedStrategy.config.stop_loss_pct ?? 2}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: colors.secondaryText }}>Target</span>
                        <span className="font-medium text-green-400">{selectedStrategy.config.target_pct ?? 4}%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Trades */}
              <div className="app-card rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
                <div className="p-1 border-b" style={{ borderColor: colors.border }}>
                  <h3 className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>Recent Trades</h3>
                </div>
                {strategyTrades.length === 0 ? (
                  <div className="p-1 text-center">
                    <p className="text-[14px]" style={{ color: colors.secondaryText }}>No trades yet</p>
                  </div>
                ) : (
                  <div className="divide-y" style={{ borderColor: colors.border }}>
                    {strategyTrades.slice(0, 5).map(trade => (
                      <div key={trade.id} className="p-1 flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-[14px] font-medium" style={{ color: colors.primaryText }}>
                              {trade.symbol.replace('.NS', '')}
                            </span>
                            <span 
                              className={`text-[13px] px-1.5 py-0.5 rounded ${
                                trade.status === 'OPEN' ? 'bg-blue-500/20 text-blue-400' :
                                trade.result === 'WIN' ? 'bg-green-500/20 text-green-400' :
                                'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {trade.status === 'OPEN' ? 'OPEN' : trade.result}
                            </span>
                          </div>
                          <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                            {trade.quantity} shares @ {formatPrice(trade.entry_price)}
                          </p>
                        </div>
                        <div className="text-right">
                          {trade.pnl !== null && (
                            <p className="text-[14px] font-medium" style={{ color: trade.pnl >= 0 ? colors.profit : colors.loss }}>
                              {trade.pnl >= 0 ? '+' : ''}{formatPrice(trade.pnl)}
                            </p>
                          )}
                          <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                            {formatTime(trade.trigger_time)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Decisions — always visible once strategy selected */}
              <div className="app-card rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
                <div className="p-1 border-b flex items-center justify-between" style={{ borderColor: colors.border }}>
                  <div className="flex items-center space-x-2">
                    <Activity className="w-4 h-4" style={{ color: colors.accent }} />
                    <h3 className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>Recent Decisions</h3>
                  </div>
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>per symbol · last 24 h</span>
                </div>
                {(selectedStrategy.stocks || []).length === 0 ? (
                  <div className="p-1 text-center">
                    <p className="text-[13px]" style={{ color: colors.secondaryText }}>No symbols configured</p>
                  </div>
                ) : (
                  <div className="divide-y" style={{ borderColor: colors.border }}>
                    {(selectedStrategy.stocks || []).map(symbol => {
                      const dec = lastDecisions[symbol];
                      const cd = cooldowns[symbol];

                      // Determine display state
                      let decColor, decIcon, badge, reason, priceLabel;
                      if (!dec) {
                        decColor = colors.secondaryText;
                        decIcon = <Clock className="w-3 h-3" style={{ color: decColor }} />;
                        badge = 'pending';
                        reason = 'Not evaluated yet';
                      } else if (dec.decision === 'triggered') {
                        decColor = '#16C784';
                        decIcon = <CheckCircle className="w-3 h-3" style={{ color: decColor }} />;
                        badge = 'triggered';
                        reason = dec.reason;
                      } else if (dec.decision === 'blocked') {
                        decColor = '#ef4444';
                        decIcon = <Ban className="w-3 h-3" style={{ color: decColor }} />;
                        badge = 'blocked';
                        reason = dec.reason;
                      } else {
                        decColor = '#eab308';
                        decIcon = <AlertCircle className="w-3 h-3" style={{ color: decColor }} />;
                        badge = 'skipped';
                        reason = dec.reason;
                      }
                      if (dec?.live_price) {
                        priceLabel = formatPrice(dec.live_price);
                      }

                      return (
                        <div key={symbol} className="px-1.5 py-0.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              {decIcon}
                              <span className="text-[13px] font-medium" style={{ color: colors.primaryText }}>
                                {symbol.replace('.NS', '')}
                              </span>
                              <span className="text-[13px] px-1.5 py-0.5 rounded-full font-medium uppercase"
                                style={{ backgroundColor: `${decColor}18`, color: decColor }}>
                                {badge}
                              </span>
                            </div>
                            <div className="flex items-center space-x-3 text-[13px]" style={{ color: colors.secondaryText }}>
                              {priceLabel && <span>{priceLabel}</span>}
                              {/* Cooldown pill */}
                              {cd?.in_cooldown ? (
                                <span className="flex items-center space-x-1 px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400">
                                  <Clock className="w-2.5 h-2.5" />
                                  <span>{cd.remaining_minutes > 0 ? `${cd.remaining_minutes}m cooldown` : `${cd.remaining_seconds}s cooldown`}</span>
                                </span>
                              ) : (
                                <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400">ready</span>
                              )}
                            </div>
                          </div>
                          <p className="text-[13px] mt-1 ml-5" style={{ color: colors.secondaryText }}>{reason}</p>
                          {dec?.conditions_met?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1 ml-5">
                              {dec.conditions_met.map(c => (
                                <span key={c.name} className="text-[13px] px-1 py-0.5 rounded"
                                  style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#16C784' }}>
                                  ✓ {c.name}
                                </span>
                              ))}
                              {dec.conditions_failed?.map(c => (
                                <span key={c.name} className="text-[13px] px-1 py-0.5 rounded"
                                  style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
                                  ✔ {c.name}
                                </span>
                              ))}
                            </div>
                          )}
                          {dec?.timestamp && (
                            <p className="text-[13px] mt-0.5 ml-5" style={{ color: colors.secondaryText }}>
                              {formatTime(dec.timestamp)}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Execution Log */}
              <div className="app-card rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
                <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: colors.border }}>
                  <h3 className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>Execution Log</h3>
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>Evaluation → Decision → Execution</span>
                </div>
                {strategyLogs.length === 0 ? (
                  <div className="p-1 text-center">
                    <p className="text-[14px]" style={{ color: colors.secondaryText }}>No activity yet</p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    {strategyLogs.slice(0, 20).map(log => {
                      const isTriggered = log.action === 'TRADE_TRIGGERED';
                      const isSkipped = log.action === 'TRADE_SKIPPED' || log.action === 'CONDITIONS_NOT_MET';
                      const isBlocked = log.action === 'COOLDOWN_ACTIVE' || log.action === 'DAILY_LIMIT_REACHED' || log.action === 'DUPLICATE_ORDER';
                      const icon = isTriggered
                        ? <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                        : isBlocked
                          ? <Ban className="w-3 h-3 text-red-400 flex-shrink-0" />
                          : isSkipped
                            ? <AlertCircle className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                            : <Clock className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />;

                      return (
                        <div key={log.id} className="px-1.5 py-0.5 border-b" style={{ borderColor: colors.border }}>
                          <div className="flex items-start space-x-2">
                            <div className="mt-0.5">{icon}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-1.5">
                                  <span className="text-[13px] font-medium" style={{ color: colors.primaryText }}>
                                    {log.action.replace(/_/g, ' ')}
                                  </span>
                                  {log.symbol && (
                                    <span className="text-[13px]" style={{ color: colors.secondaryText }}>
                                      · {log.symbol.replace('.NS', '')}
                                    </span>
                                  )}
                                </div>
                                <span className="text-[13px] flex-shrink-0 ml-2" style={{ color: colors.secondaryText }}>
                                  {formatTime(log.execution_time)}
                                </span>
                              </div>

                              {/* Reason */}
                              {log.details?.reason && (
                                <p className="text-[13px] mt-0.5" style={{ color: colors.secondaryText }}>
                                  {log.details.reason}
                                </p>
                              )}

                              {/* Triggered trade details: entry / SL / target / conditions */}
                              {isTriggered && (
                                <div className="mt-1.5 space-y-1">
                                  {(log.details?.entry_price || log.details?.limit_price) && (
                                    <div className="flex items-center gap-3 text-[13px]">
                                      <span style={{ color: colors.secondaryText }}>Entry</span>
                                      <span className="font-medium" style={{ color: colors.primaryText }}>
                                        {formatPrice(log.details.entry_price || log.details.limit_price)}
                                      </span>
                                      {log.details?.stop_loss && (
                                        <>
                                          <span style={{ color: colors.secondaryText }}>SL</span>
                                          <span className="font-medium text-red-400">{formatPrice(log.details.stop_loss)}</span>
                                        </>
                                      )}
                                      {log.details?.target && (
                                        <>
                                          <span style={{ color: colors.secondaryText }}>Target</span>
                                          <span className="font-medium text-green-400">{formatPrice(log.details.target)}</span>
                                        </>
                                      )}
                                    </div>
                                  )}
                                  {log.details?.conditions_met?.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {log.details.conditions_met.map(c => (
                                        <span key={c.name || c} className="text-[13px] px-1 py-0.5 rounded"
                                          style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#16C784' }}>
                                          ✓ {c.name || c}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Discipline Score */}
              {discipline && (() => {
                const score = discipline.score ?? 100;
                const level = discipline.level ?? 'EXCELLENT';
                const scoreColor = score >= 85 ? '#16C784' : score >= 70 ? '#6366F1' : score >= 50 ? '#f59e0b' : '#ef4444';
                const levelBg = score >= 85 ? 'rgba(34,197,94,0.1)' : score >= 70 ? 'rgba(59,130,246,0.1)' : score >= 50 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)';
                const EVENT_LABELS = {
                  PANIC_PAUSE: { label: 'Panic Pause', icon: '!', color: '#ef4444' },
                  CONFIG_CHANGE_ACTIVE: { label: 'Config Changed (Active)', icon: 'CFG', color: '#f59e0b' },
                  MANUAL_OVERRIDE: { label: 'Manual Override', icon: 'MAN', color: '#f59e0b' },
                  HELD_THROUGH_LOSS: { label: 'Held Through Loss', icon: 'OK', color: '#16C784' },
                };
                return (
                  <div className="app-card rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
                    <div className="p-1 border-b flex items-center justify-between" style={{ borderColor: colors.border }}>
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>Discipline Score</h3>
                        <div className="relative">
                          <button
                            onClick={() => setShowDisciplineInfo(v => !v)}
                            className="flex items-center justify-center w-4 h-4 rounded-full opacity-60 hover:opacity-100 transition-opacity"
                            style={{ color: colors.secondaryText }}
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                          {showDisciplineInfo && (
                            <div className="app-card absolute left-0 top-5 z-20 w-72 rounded-lg p-1 shadow-xl text-[14px] leading-relaxed"
                              style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.accent, color: colors.secondaryText }}>
                              <p className="font-semibold mb-1.5" style={{ color: colors.primaryText }}>Algo Discipline vs Manual Discipline</p>
                              <p className="mb-2">
                                In <span className="font-medium" style={{ color: colors.primaryText }}>manual trading</span>, discipline means <em>following your own plan</em> — setting stop losses, not chasing stocks, not overtrading.
                              </p>
                              <p className="mb-2">
                                In <span className="font-medium" style={{ color: colors.primaryText }}>algo trading</span>, the engine already enforces your rules. Discipline here means <em>not interfering with your own system</em>.
                              </p>
                              <p className="font-medium mb-1" style={{ color: colors.primaryText }}>This score tracks algo interference:</p>
                              <ul className="space-y-1 pl-1">
                                <li>! <b>Panic Pause</b> - pausing while trades are open (-20)</li>
                                <li>CFG <b>Config Change</b> - tweaking thresholds while ACTIVE (-10)</li>
                                <li>MAN <b>Manual Override</b> - buying manually when algo said skip (-15)</li>
                                <li>OK <b>Held Through Loss</b> - staying patient through drawdown (+10)</li>
                              </ul>
                              <p className="mt-2 italic">A score of 100 means you built a system and trusted it.</p>
                              <button onClick={() => setShowDisciplineInfo(false)} className="mt-2 text-[13px] opacity-60 hover:opacity-100">Dismiss</button>
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="text-[13px]" style={{ color: colors.secondaryText }}>7-day algo interference score</span>
                    </div>
                    <div className="p-1">
                      {/* Score row */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative w-14 h-14 flex-shrink-0">
                          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
                            <circle cx="18" cy="18" r="14" fill="none" stroke={colors.border} strokeWidth="3" />
                            <circle cx="18" cy="18" r="14" fill="none" stroke={scoreColor} strokeWidth="3"
                              strokeDasharray={`${(score / 100) * 87.96} 87.96`} strokeLinecap="round" />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-[13px] font-bold" style={{ color: scoreColor }}>{score}</span>
                        </div>
                        <div>
                          <span className="text-[13px] font-semibold px-1 py-0.5 rounded" style={{ backgroundColor: levelBg, color: scoreColor }}>{level}</span>
                          <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[13px]" style={{ color: colors.secondaryText }}>
                            <span>Panic pauses: <b style={{ color: discipline.stats.panic_pauses > 0 ? '#ef4444' : colors.primaryText }}>{discipline.stats.panic_pauses}</b></span>
                            <span>Config changes: <b style={{ color: discipline.stats.config_changes > 0 ? '#f59e0b' : colors.primaryText }}>{discipline.stats.config_changes}</b></span>
                            <span>Manual overrides: <b style={{ color: discipline.stats.manual_overrides > 0 ? '#f59e0b' : colors.primaryText }}>{discipline.stats.manual_overrides}</b></span>
                            <span>Held through loss: <b style={{ color: discipline.stats.held_through_loss > 0 ? '#16C784' : colors.primaryText }}>{discipline.stats.held_through_loss}</b></span>
                          </div>
                        </div>
                      </div>

                      {/* Score bar */}
                      <div className="w-full rounded-full h-1.5 mb-3" style={{ backgroundColor: colors.border }}>
                        <div className="h-1.5 rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: scoreColor }} />
                      </div>

                      {/* Recent events */}
                      {discipline.events.length > 0 ? (
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {discipline.events.slice(0, 8).map(ev => {
                            const meta = EVENT_LABELS[ev.event_type] || { label: ev.event_type, icon: 'o', color: colors.secondaryText };
                            return (
                              <div key={ev.id} className="flex items-start justify-between text-[13px] py-0.5 border-b" style={{ borderColor: colors.border }}>
                                <div className="flex items-start gap-1.5">
                                  <span>{meta.icon}</span>
                                  <div>
                                    <span style={{ color: meta.color }}>{meta.label}</span>
                                    {ev.details?.reason && (
                                      <p className="mt-0.5" style={{ color: colors.secondaryText }}>{ev.details.reason}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                                  <span className="font-medium" style={{ color: ev.score_impact < 0 ? '#ef4444' : '#16C784' }}>
                                    {ev.score_impact > 0 ? '+' : ''}{ev.score_impact}
                                  </span>
                                  <span style={{ color: colors.secondaryText }}>
                                    {ev.event_time ? formatDate(ev.event_time) : ''}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-[14px] text-center py-0.5" style={{ color: colors.secondaryText }}>No behavioral events yet — keep trading with discipline!</p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="app-card rounded-2xl p-8 text-center" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, #8b5cf608 100%)`, border: `1px solid ${colors.accent}25` }}>
              <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, #8b5cf610 100%)`, border: `1px solid ${colors.accent}30` }}>
                <Settings className="w-8 h-8" style={{ color: colors.accent }} />
              </div>
              <h3 className="text-[15px] font-semibold" style={{ color: colors.primaryText }}>Select a Strategy</h3>
              <p className="text-[13px] mt-1.5" style={{ color: colors.secondaryText }}>
                Choose a strategy from the list to view details and manage trades
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Report Card Modal */}
      {showReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-1">
          <div className="app-card rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            style={{ background: `linear-gradient(135deg, ${colors.accent}40 0%, ${colors.accent}15 100%), ${colors.cardBg}`, border: `1px solid ${colors.accentBorder}` }}>
            {/* Header */}
            <div className="flex items-center justify-between p-1 border-b flex-shrink-0" style={{ borderColor: colors.border }}>
              <div>
                <h2 className="text-[14px] font-bold" style={{ color: colors.primaryText }}>
                  Strategy Report Card
                </h2>
                {report && <p className="text-[13px] mt-0.5" style={{ color: colors.secondaryText }}>{report.strategy_name}</p>}
              </div>
              <button onClick={() => setShowReport(false)} className="p-1 rounded-lg hover:opacity-70">
                <X className="w-5 h-5" style={{ color: colors.secondaryText }} />
              </button>
            </div>

            {reportLoading ? (
              <div className="flex-1 flex items-center justify-center p-1">
                <RefreshCw className="w-6 h-6 animate-spin" style={{ color: colors.accent }} />
              </div>
            ) : report && (
              <div className="flex-1 overflow-y-auto p-1 space-y-4">

                {/* Performance + Risk — side by side */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Performance */}
                  <div className="rounded-lg p-1" style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: colors.border }}>
                    <p className="text-[13px] font-semibold mb-2" style={{ color: colors.primaryText }}>Performance</p>
                    <div className="space-y-1.5 text-[14px]">
                      {[
                        ['Closed trades', report.performance.closed_trades],
                        ['Win rate', `${report.performance.win_rate}%`],
                        ['Total P&L', formatPrice(report.performance.total_pnl)],
                        ['Profit factor', report.performance.profit_factor],
                        ['Avg hold', `${report.performance.avg_hold_hours}h`],
                      ].map(([label, val]) => (
                        <div key={label} className="flex justify-between">
                          <span style={{ color: colors.secondaryText }}>{label}</span>
                          <span className="font-medium" style={{ color: colors.primaryText }}>{val}</span>
                        </div>
                      ))}
                      {report.performance.best_trade && (
                        <div className="flex justify-between">
                          <span style={{ color: colors.secondaryText }}>Best trade</span>
                          <span className="font-medium text-green-400">{report.performance.best_trade.symbol?.replace('.NS','')}: +{formatPrice(report.performance.best_trade.pnl)}</span>
                        </div>
                      )}
                      {report.performance.worst_trade && (
                        <div className="flex justify-between">
                          <span style={{ color: colors.secondaryText }}>Worst trade</span>
                          <span className="font-medium text-red-400">{report.performance.worst_trade.symbol?.replace('.NS','')}: {formatPrice(report.performance.worst_trade.pnl)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Risk + Execution */}
                  <div className="rounded-lg p-1" style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: colors.border }}>
                    <p className="text-[13px] font-semibold mb-2" style={{ color: colors.primaryText }}>Risk & Execution</p>
                    <div className="space-y-1.5 text-[14px]">
                      <div className="flex justify-between">
                        <span style={{ color: colors.secondaryText }}>Avg risk score</span>
                        <span className="font-medium" style={{ color: report.risk_profile.avg_risk_score > 65 ? '#ef4444' : report.risk_profile.avg_risk_score > 39 ? '#f59e0b' : '#16C784' }}>
                          {report.risk_profile.avg_risk_score ?? '—'}
                        </span>
                      </div>
                      {['LOW','MODERATE','HIGH'].map(lvl => (
                        <div key={lvl} className="flex justify-between">
                          <span style={{ color: colors.secondaryText }}>{lvl}</span>
                          <span className="font-medium" style={{ color: lvl === 'HIGH' ? '#ef4444' : lvl === 'MODERATE' ? '#f59e0b' : '#16C784' }}>
                            {report.risk_profile.distribution[lvl] ?? 0} trades
                          </span>
                        </div>
                      ))}
                      <div className="border-t pt-0.5 mt-1" style={{ borderColor: colors.border }}>
                        <div className="flex justify-between">
                          <span style={{ color: colors.secondaryText }}>Trigger rate</span>
                          <span className="font-medium" style={{ color: colors.primaryText }}>{report.execution.trigger_rate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: colors.secondaryText }}>Total evaluations</span>
                          <span className="font-medium" style={{ color: colors.primaryText }}>{report.execution.total_evaluations}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Conditions hit rate */}
                {report.conditions.length > 0 && (
                  <div className="rounded-lg p-1" style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: colors.border }}>
                    <p className="text-[13px] font-semibold mb-2" style={{ color: colors.primaryText }}>Condition Hit Rate</p>
                    <div className="space-y-1.5">
                      {report.conditions.map(c => (
                        <div key={c.condition} className="flex items-center gap-2 text-[14px]">
                          <span className="w-36 truncate flex-shrink-0" style={{ color: colors.secondaryText }}>{c.condition.replace(/_/g,' ')}</span>
                          <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: colors.border }}>
                            <div className="h-1.5 rounded-full" style={{ width: `${c.win_rate}%`, backgroundColor: c.win_rate >= 60 ? '#16C784' : c.win_rate >= 40 ? '#f59e0b' : '#ef4444' }} />
                          </div>
                          <span className="w-10 text-right" style={{ color: c.win_rate >= 60 ? '#16C784' : c.win_rate >= 40 ? '#f59e0b' : '#ef4444' }}>{c.win_rate}%</span>
                          <span className="w-12 text-right" style={{ color: colors.secondaryText }}>{c.triggers} fires</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top block reasons */}
                {report.execution.top_block_reasons.length > 0 && (
                  <div className="rounded-lg p-1" style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: colors.border }}>
                    <p className="text-[13px] font-semibold mb-2" style={{ color: colors.primaryText }}>Why Trades Are Blocked</p>
                    <div className="space-y-1">
                      {report.execution.top_block_reasons.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-[14px]">
                          <span className="truncate flex-1 mr-2" style={{ color: colors.secondaryText }}>{r.reason}</span>
                          <span className="flex-shrink-0 font-medium" style={{ color: colors.primaryText }}>{r.count}×</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Discipline */}
                <div className="rounded-lg p-1" style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: colors.border }}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Interference Score</p>
                    <span className="text-[13px] font-bold px-1 py-0.5 rounded" style={{
                      backgroundColor: report.discipline.score >= 85 ? 'rgba(34,197,94,0.1)' : report.discipline.score >= 70 ? 'rgba(59,130,246,0.1)' : report.discipline.score >= 50 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                      color: report.discipline.score >= 85 ? '#16C784' : report.discipline.score >= 70 ? '#6366F1' : report.discipline.score >= 50 ? '#f59e0b' : '#ef4444',
                    }}>{report.discipline.score} — {report.discipline.level}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 text-[14px]" style={{ color: colors.secondaryText }}>
                    {Object.entries(report.discipline.event_counts).map(([k, v]) => (
                      <span key={k}>{k.replace(/_/g,' ')}: <b style={{ color: colors.primaryText }}>{v}</b></span>
                    ))}
                    <span>Total deductions: <b style={{ color: '#ef4444' }}>-{report.discipline.total_deductions}</b></span>
                  </div>
                </div>

                {/* Patterns */}
                <div className="rounded-lg p-1" style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: colors.border }}>
                  <p className="text-[13px] font-semibold mb-2" style={{ color: colors.primaryText }}>Behavioral Patterns</p>
                  <div className="space-y-2">
                    {report.patterns.map((p, i) => (
                      <div key={i} className="flex items-start gap-2 text-[14px]">
                        <span className="flex-shrink-0 mt-0.5">
                          {p.type === 'warning' ? 'Warning' : p.type === 'positive' ? 'OK' : 'Info'}
                        </span>
                        <div>
                          <span className="font-medium" style={{ color: colors.primaryText }}>{p.title}</span>
                          <p className="mt-0.5" style={{ color: colors.secondaryText }}>{p.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommendations */}
                <div className="rounded-lg p-1" style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: colors.border }}>
                  <p className="text-[13px] font-semibold mb-2" style={{ color: colors.primaryText }}>Recommendations</p>
                  <ul className="space-y-1.5">
                    {report.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[14px]" style={{ color: colors.secondaryText }}>
                        <span className="flex-shrink-0 mt-0.5 text-[13px]" style={{ color: colors.accent }}>→</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" data-testid="strategy-modal">
          <div
            className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl"
            style={{
              backgroundColor: colors.cardBg,
              border: `1px solid ${colors.accent}35`,
              boxShadow: `0 -24px 60px rgba(0,0,0,0.7), 0 0 0 1px ${colors.accent}12`,
            }}
          >
            {/* Rainbow top accent bar */}
            <div className="h-1 rounded-t-3xl sm:rounded-t-2xl" style={{ background: `linear-gradient(90deg, ${colors.accent}, #8b5cf6, #06b6d4, #10b981)` }} />

            <div className="p-4 sm:p-5">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${colors.accent}30 0%, ${colors.accent}10 100%)`, border: `1px solid ${colors.accent}40` }}>
                    <FlaskConical className="w-5 h-5" style={{ color: colors.accent }} />
                  </div>
                  <div>
                    <h2 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>
                      {editingStrategy ? 'Edit Strategy' : 'Create New Strategy'}
                    </h2>
                    <p className="text-[11px]" style={{ color: colors.secondaryText }}>
                      {editingStrategy ? 'Update your algo strategy settings' : 'Define your automated trading rules'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="p-2 rounded-full transition-all"
                  style={{ background: `linear-gradient(135deg, ${colors.accent}15 0%, ${colors.accent}05 100%)`, border: `1px solid ${colors.accent}25` }}
                >
                  <X className="w-4 h-4" style={{ color: colors.secondaryText }} />
                </button>
              </div>

            <div className="space-y-5">
              {/* ── BASIC INFO ── */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: colors.accent }}>Basic Info</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[12px] font-medium mb-1.5" style={{ color: colors.secondaryText }}>Strategy Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl text-[14px]"
                      style={{ background: `linear-gradient(135deg, ${colors.accent}08 0%, ${colors.accent}03 100%)`, border: `1px solid ${colors.accent}30`, color: colors.primaryText, outline: 'none' }}
                      placeholder="e.g., RSI Reversal Strategy"
                      data-testid="strategy-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium mb-1.5" style={{ color: colors.secondaryText }}>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl text-[14px] resize-none"
                      rows={2}
                      style={{ background: `linear-gradient(135deg, ${colors.accent}08 0%, ${colors.accent}03 100%)`, border: `1px solid ${colors.accent}30`, color: colors.primaryText, outline: 'none' }}
                      placeholder="Describe your strategy..."
                    />
                  </div>
                </div>
              </div>

              {/* ── RISK SETTINGS ── */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: '#f59e0b' }}>Risk Settings</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-[12px] font-medium mb-1.5" style={{ color: colors.secondaryText }}>Capital Allocated (₹)</label>
                    <input
                      type="number"
                      value={formData.capital_allocated}
                      onChange={(e) => setFormData({ ...formData, capital_allocated: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2.5 rounded-xl text-[14px]"
                      style={{ background: `linear-gradient(135deg, #f59e0b10 0%, #f59e0b04 100%)`, border: `1px solid #f59e0b30`, color: colors.primaryText, outline: 'none' }}
                      data-testid="capital-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium mb-1.5" style={{ color: colors.secondaryText }}>Risk per Trade (%)</label>
                    <div className="flex items-center rounded-xl overflow-hidden"
                      style={{ background: `linear-gradient(135deg, #f59e0b12 0%, #f59e0b04 100%)`, border: `1px solid #f59e0b40` }}>
                      <button
                        onClick={() => setFormData(f => ({ ...f, risk_per_trade: Math.max(0.1, parseFloat((f.risk_per_trade - 0.1).toFixed(1))) }))}
                        className="px-3 py-2.5 text-[18px] font-bold leading-none"
                        style={{ color: '#f59e0b' }}>−</button>
                      <span className="flex-1 text-center text-[15px] font-bold" style={{ color: colors.primaryText }}>
                        {formData.risk_per_trade}%
                      </span>
                      <button
                        onClick={() => setFormData(f => ({ ...f, risk_per_trade: Math.min(10, parseFloat((f.risk_per_trade + 0.1).toFixed(1))) }))}
                        className="px-3 py-2.5 text-[18px] font-bold leading-none"
                        style={{ color: '#f59e0b' }}>+</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium mb-1.5" style={{ color: colors.secondaryText }}>Max Trades / Day</label>
                    <div className="flex items-center rounded-xl overflow-hidden"
                      style={{ background: `linear-gradient(135deg, #f59e0b12 0%, #f59e0b04 100%)`, border: `1px solid #f59e0b40` }}>
                      <button
                        onClick={() => setFormData(f => ({ ...f, max_trades_per_day: Math.max(1, f.max_trades_per_day - 1) }))}
                        className="px-3 py-2.5 text-[18px] font-bold leading-none"
                        style={{ color: '#f59e0b' }}>−</button>
                      <span className="flex-1 text-center text-[15px] font-bold" style={{ color: colors.primaryText }}>
                        {formData.max_trades_per_day}
                      </span>
                      <button
                        onClick={() => setFormData(f => ({ ...f, max_trades_per_day: Math.min(20, f.max_trades_per_day + 1) }))}
                        className="px-3 py-2.5 text-[18px] font-bold leading-none"
                        style={{ color: '#f59e0b' }}>+</button>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[12px] font-medium mb-1.5" style={{ color: colors.secondaryText }}>Cooldown</label>
                    <CustomSelect
                      value={formData.cooldown_minutes}
                      onChange={(v) => setFormData({ ...formData, cooldown_minutes: parseInt(v) })}
                      options={[
                        { value: 60, label: '1 hour' },
                        { value: 240, label: '4 hours' },
                        { value: 480, label: '8 hours' },
                        { value: 1440, label: '1 day' },
                        { value: 2880, label: '2 days' },
                        { value: 4320, label: '3 days' },
                        { value: 10080, label: '1 week' },
                      ]}
                      colors={colors}
                      className="px-3 py-2.5 rounded-xl"
                      style={{ background: `linear-gradient(135deg, #f59e0b10 0%, #f59e0b04 100%)`, border: `1px solid #f59e0b30`, color: colors.primaryText }}
                    />
                  </div>
                </div>
              </div>

              {/* ── ENGINE RULES ── */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: '#8b5cf6' }}>Engine Rules</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                </div>
                <div className="rounded-2xl p-3 space-y-3"
                  style={{ background: `linear-gradient(135deg, #8b5cf614 0%, #8b5cf604 100%)`, border: `1px solid #8b5cf630` }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] mb-1.5" style={{ color: colors.secondaryText }}>Entry Logic</label>
                      <CustomSelect value={formData.entry_logic}
                        onChange={v => setFormData({ ...formData, entry_logic: v })}
                        options={[{ value: 'ALL', label: 'ALL conditions must match' }, { value: 'ANY', label: 'ANY condition can match' }]}
                        colors={colors} className="px-3 py-2.5 rounded-xl"
                        style={{ background: `linear-gradient(135deg, #8b5cf620 0%, #8b5cf608 100%)`, border: `1px solid #8b5cf640`, color: colors.primaryText }} />
                    </div>
                    <div>
                      <label className="block text-[12px] mb-1.5" style={{ color: colors.secondaryText }}>Trend Filter</label>
                      <CustomSelect value={formData.trend_filter}
                        onChange={v => setFormData({ ...formData, trend_filter: v })}
                        options={[{ value: 'ANY', label: 'Any trend' }, { value: 'UPTREND', label: 'Uptrend only (price > SMA50)' }, { value: 'DOWNTREND', label: 'Downtrend only (price < SMA50)' }]}
                        colors={colors} className="px-3 py-2.5 rounded-xl"
                        style={{ background: `linear-gradient(135deg, #8b5cf620 0%, #8b5cf608 100%)`, border: `1px solid #8b5cf640`, color: colors.primaryText }} />
                    </div>
                    <div>
                      <label className="block text-[12px] mb-1.5" style={{ color: colors.secondaryText }}>Execution Mode</label>
                      <CustomSelect value={formData.execution_mode}
                        onChange={v => setFormData({ ...formData, execution_mode: v })}
                        options={[{ value: 'INSTANT', label: 'Instant (at live price)' }, { value: 'CANDLE_CLOSE', label: 'Candle Close' }]}
                        colors={colors} className="px-3 py-2.5 rounded-xl"
                        style={{ background: `linear-gradient(135deg, #8b5cf620 0%, #8b5cf608 100%)`, border: `1px solid #8b5cf640`, color: colors.primaryText }} />
                    </div>
                    <div>
                      <label className="block text-[12px] mb-1.5" style={{ color: colors.secondaryText }}>Exit Type</label>
                      <CustomSelect value={formData.exit_type}
                        onChange={v => setFormData({ ...formData, exit_type: v })}
                        options={[{ value: 'RR', label: 'R:R (SL / Target hit)' }, { value: 'TIME', label: 'Time-based (candles)' }]}
                        colors={colors} className="px-3 py-2.5 rounded-xl"
                        style={{ background: `linear-gradient(135deg, #8b5cf620 0%, #8b5cf608 100%)`, border: `1px solid #8b5cf640`, color: colors.primaryText }} />
                    </div>
                  </div>

                  {/* SL + Target with color coding */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] mb-1.5 font-semibold" style={{ color: '#ef4444' }}>Stop Loss %</label>
                      <div className="flex items-center rounded-xl overflow-hidden"
                        style={{ background: `linear-gradient(135deg, #ef444418 0%, #ef444406 100%)`, border: `1px solid #ef444440` }}>
                        <button onClick={() => setFormData(f => ({ ...f, stop_loss_pct: Math.max(0.5, parseFloat((f.stop_loss_pct - 0.5).toFixed(1))) }))}
                          className="px-3 py-2.5 text-[18px] font-bold leading-none" style={{ color: '#ef4444' }}>−</button>
                        <span className="flex-1 text-center text-[15px] font-bold" style={{ color: '#ef4444' }}>
                          {formData.stop_loss_pct}%
                        </span>
                        <button onClick={() => setFormData(f => ({ ...f, stop_loss_pct: Math.min(20, parseFloat((f.stop_loss_pct + 0.5).toFixed(1))) }))}
                          className="px-3 py-2.5 text-[18px] font-bold leading-none" style={{ color: '#ef4444' }}>+</button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[12px] mb-1.5 font-semibold" style={{ color: colors.profit }}>Target %</label>
                      <div className="flex items-center rounded-xl overflow-hidden"
                        style={{ background: `linear-gradient(135deg, ${colors.profit}18 0%, ${colors.profit}06 100%)`, border: `1px solid ${colors.profit}40` }}>
                        <button onClick={() => setFormData(f => ({ ...f, target_pct: Math.max(0.5, parseFloat((f.target_pct - 0.5).toFixed(1))) }))}
                          className="px-3 py-2.5 text-[18px] font-bold leading-none" style={{ color: colors.profit }}>−</button>
                        <span className="flex-1 text-center text-[15px] font-bold" style={{ color: colors.profit }}>
                          {formData.target_pct}%
                        </span>
                        <button onClick={() => setFormData(f => ({ ...f, target_pct: Math.min(50, parseFloat((f.target_pct + 0.5).toFixed(1))) }))}
                          className="px-3 py-2.5 text-[18px] font-bold leading-none" style={{ color: colors.profit }}>+</button>
                      </div>
                    </div>
                  </div>

                  {/* Edge Report Gate */}
                  <div className="pt-3 border-t" style={{ borderColor: '#8b5cf625' }}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Edge Report Gate</p>
                        <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>Only trigger if Edge Report meets minimum confidence</p>
                      </div>
                      <button
                        onClick={() => setFormData(f => ({ ...f, edge_gate_enabled: !f.edge_gate_enabled }))}
                        className="px-3 py-1.5 rounded-full text-[12px] font-bold transition-all"
                        style={{
                          background: formData.edge_gate_enabled
                            ? `linear-gradient(135deg, ${colors.profit}30 0%, ${colors.profit}15 100%)`
                            : `linear-gradient(135deg, ${colors.accent}15 0%, ${colors.accent}05 100%)`,
                          color: formData.edge_gate_enabled ? colors.profit : colors.secondaryText,
                          border: `1px solid ${formData.edge_gate_enabled ? colors.profit + '50' : colors.border}`,
                        }}>
                        {formData.edge_gate_enabled ? 'ON' : 'OFF'}
                      </button>
                    </div>
                    {formData.edge_gate_enabled && (
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="block text-[12px] mb-1" style={{ color: colors.secondaryText }}>Min Confidence</label>
                          <div className="flex items-center gap-2">
                            <input type="range" min={50} max={85} step={5}
                              value={formData.edge_gate_min_confidence}
                              onChange={e => setFormData(f => ({ ...f, edge_gate_min_confidence: parseInt(e.target.value) }))}
                              className="flex-1" />
                            <span className="text-[13px] font-bold w-10 text-right" style={{ color: colors.profit }}>
                              {formData.edge_gate_min_confidence}%
                            </span>
                          </div>
                        </div>
                        <div className="text-[11px]" style={{ color: colors.secondaryText }}>
                          Allowed: SWING BUY, BUY, INVEST
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── STOCK UNIVERSE ── */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: '#06b6d4' }}>Stock Universe</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                </div>

                {/* Watchlist live-source picker */}
                <div className="mb-3 rounded-2xl p-3"
                  style={{ background: `linear-gradient(135deg, #06b6d414 0%, #06b6d404 100%)`, border: `1px solid #06b6d430` }}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>Watchlist Source</label>
                    {formData.watchlist_id && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: `linear-gradient(135deg, #06b6d425 0%, #06b6d410 100%)`, color: '#06b6d4', border: `1px solid #06b6d450` }}>
                        Live-linked
                      </span>
                    )}
                  </div>
                  <CustomSelect
                    value={formData.watchlist_id || ''}
                    onChange={v => setFormData(f => ({ ...f, watchlist_id: v }))}
                    options={[
                      { value: '', label: 'None (manual stocks only)' },
                      ...watchlists.map(wl => ({ value: wl.id, label: `${wl.name} (${(wl.stocks || []).length} stocks)` })),
                    ]}
                    colors={colors} className="px-3 py-2.5 rounded-xl"
                    style={{ background: `linear-gradient(135deg, #06b6d420 0%, #06b6d408 100%)`, border: `1px solid #06b6d440`, color: colors.primaryText }}
                  />
                  {formData.watchlist_id && (
                    <p className="text-[11px] mt-2" style={{ color: colors.secondaryText }}>
                      Watchlist stocks are live-linked — changes apply automatically at next evaluation.
                    </p>
                  )}
                </div>

                {/* Selected stock chips */}
                {formData.stocks.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {formData.stocks.map(symbol => (
                      <span key={symbol}
                        className="flex items-center gap-1 px-2 py-1 rounded-full text-[12px] font-medium"
                        style={{ background: `linear-gradient(135deg, #06b6d425 0%, #06b6d410 100%)`, color: '#06b6d4', border: `1px solid #06b6d450` }}>
                        {symbol.replace('.NS', '')}
                        <button onClick={() => toggleStock(symbol)} className="opacity-60 hover:opacity-100 leading-none ml-0.5">×</button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Collapsible stock grid */}
                <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid #06b6d430` }}>
                  <button
                    onClick={() => setStockGridOpen(o => !o)}
                    className="w-full flex items-center justify-between px-3 py-2.5"
                    style={{ background: `linear-gradient(135deg, #06b6d418 0%, #06b6d408 100%)` }}>
                    <span className="text-[12px] font-bold uppercase tracking-wide" style={{ color: '#06b6d4' }}>
                      Manual Stocks
                    </span>
                    <div className="flex items-center gap-2">
                      {formData.stocks.length > 0 && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ backgroundColor: '#06b6d430', color: '#06b6d4' }}>
                          {formData.stocks.length}
                        </span>
                      )}
                      <span className="text-[12px]" style={{ color: colors.secondaryText }}>
                        {stockGridOpen ? '▲' : '▼'}
                      </span>
                    </div>
                  </button>

                  {stockGridOpen && (
                    <div style={{ background: `linear-gradient(135deg, #06b6d410 0%, #06b6d405 100%)` }}>
                      <div className="px-3 pt-2 pb-1.5 flex items-center gap-2">
                        <input
                          type="text" placeholder="Search stocks..." value={stockSearch}
                          onChange={e => setStockSearch(e.target.value)}
                          className="flex-1 px-2 py-1.5 rounded-lg text-[13px]"
                          style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, color: colors.primaryText, outline: 'none' }}
                        />
                        <button
                          onClick={() => setFormData(f => ({ ...f, stocks: [...availableStocks] }))}
                          className="text-[12px] px-2 py-1.5 rounded-lg whitespace-nowrap font-medium"
                          style={{ background: `linear-gradient(135deg, #06b6d425 0%, #06b6d410 100%)`, color: '#06b6d4' }}>
                          All
                        </button>
                        {formData.stocks.length > 0 && (
                          <button onClick={() => setFormData(f => ({ ...f, stocks: [] }))}
                            className="text-[12px] whitespace-nowrap" style={{ color: colors.secondaryText }}>
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 px-3 pb-2">
                        {availableStocks
                          .filter(s => !stockSearch || s.toLowerCase().includes(stockSearch.toLowerCase()))
                          .map(symbol => {
                            const selected = formData.stocks.includes(symbol);
                            return (
                              <button key={symbol} onClick={() => toggleStock(symbol)}
                                className="px-2 py-1.5 rounded-lg text-[12px] transition-colors"
                                style={{
                                  background: selected ? `linear-gradient(135deg, #06b6d425 0%, #06b6d410 100%)` : colors.inputBg,
                                  color: selected ? '#06b6d4' : colors.secondaryText,
                                  border: `1px solid ${selected ? '#06b6d450' : colors.border}`,
                                }}>
                                {symbol.replace('.NS', '')}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── ENTRY CONDITIONS ── */}
              <div>
                {/* Section header */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: colors.accent }}>Entry Conditions</span>
                  {formData.conditions.length > 0 && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full font-bold"
                      style={{ background: `linear-gradient(135deg, ${colors.accent}30 0%, ${colors.accent}15 100%)`, color: colors.accent }}>
                      {formData.conditions.length}
                    </span>
                  )}
                  <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                  {formData.conditions.length > 0 && (
                    <button onClick={() => setFormData(f => ({ ...f, conditions: [] }))}
                      className="text-[11px]" style={{ color: colors.secondaryText }}>
                      Clear all
                    </button>
                  )}
                </div>

                {/* ① Idea #4 — Direction wizard */}
                <div className="flex gap-2 mb-3">
                  {[
                    { value: 'ALL',     label: 'All Signals', icon: '⚡', color: colors.accent },
                    { value: 'bullish', label: 'Bullish',     icon: '▲',  color: colors.profit },
                    { value: 'bearish', label: 'Bearish',     icon: '▼',  color: colors.loss },
                  ].map(({ value, label, icon, color }) => {
                    const active = dirFilter === value;
                    return (
                      <button key={value} onClick={() => setDirFilter(value)}
                        className="flex-1 py-2.5 rounded-xl text-[12px] font-bold transition-all"
                        style={{
                          background: active ? `linear-gradient(135deg, ${color}30 0%, ${color}12 100%)` : 'transparent',
                          border: `1px solid ${active ? color + '60' : colors.border}`,
                          color: active ? color : colors.secondaryText,
                        }}>
                        {icon} {label}
                      </button>
                    );
                  })}
                </div>

                {/* ① Idea #1 — Selected condition chips (plain names) */}
                {formData.conditions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {formData.conditions.map(cond => {
                      const meta = CONDITION_BY_NAME[cond.name] || {};
                      const dirColor = meta.direction === 'bullish' ? colors.profit
                        : meta.direction === 'bearish' ? colors.loss : colors.accent;
                      return (
                        <span key={cond.name}
                          className="flex items-center gap-1 px-2 py-1 rounded-full text-[12px] font-medium"
                          style={{ background: `linear-gradient(135deg, ${dirColor}25 0%, ${dirColor}10 100%)`, color: dirColor, border: `1px solid ${dirColor}50` }}>
                          {meta.direction === 'bullish' ? '▲' : meta.direction === 'bearish' ? '▼' : '◆'} {meta.display || cond.name}
                          <button onClick={() => setFormData(f => ({ ...f, conditions: f.conditions.filter(c => c.name !== cond.name) }))}
                            className="opacity-60 hover:opacity-100 leading-none ml-0.5">×</button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Search — also searches descriptions */}
                <input
                  type="text" placeholder="Search by name or keyword (e.g. bounce, trend, breakout)…" value={condSearch}
                  onChange={e => setCondSearch(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl text-[13px] mb-3"
                  style={{ background: `linear-gradient(135deg, ${colors.accent}08 0%, ${colors.accent}03 100%)`, border: `1px solid ${colors.accent}25`, color: colors.primaryText, outline: 'none' }}
                />

                {/* ② Idea #2 + #3 — Category accordion with card-based conditions */}
                <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${colors.accent}25` }}>
                  {CONDITION_CATEGORIES.map((cat, idx) => {
                    const visible = cat.conditions.filter(c => {
                      const matchDir = dirFilter === 'ALL'
                        || c.direction === dirFilter
                        || (dirFilter === 'bullish' && c.direction === 'neutral')
                        || (dirFilter === 'bearish' && c.direction === 'neutral');
                      const q = condSearch.toLowerCase();
                      const matchSearch = !condSearch
                        || c.display.toLowerCase().includes(q)
                        || (c.desc || '').toLowerCase().includes(q);
                      return matchDir && matchSearch;
                    });
                    if (!visible.length) return null;
                    const selectedCount = cat.conditions.filter(c => formData.conditions.some(fc => fc.name === c.name)).length;
                    const isOpen = condSearch || dirFilter !== 'ALL' ? true : !!expandedCats[cat.key];
                    return (
                      <div key={cat.key} style={{ borderTop: idx > 0 ? `1px solid ${colors.accent}18` : 'none' }}>
                        {/* Category header */}
                        <button onClick={() => toggleCat(cat.key)}
                          className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                          style={{ background: `linear-gradient(135deg, ${colors.accent}10 0%, ${colors.accent}04 100%)` }}>
                          <span className="text-[12px] font-bold uppercase tracking-wide" style={{ color: colors.accent }}>{cat.label}</span>
                          <div className="flex items-center gap-2">
                            {selectedCount > 0 && (
                              <span className="text-[11px] px-1.5 py-0.5 rounded-full font-bold"
                                style={{ backgroundColor: colors.accent + '30', color: colors.accent }}>{selectedCount}</span>
                            )}
                            <span className="text-[12px]" style={{ color: colors.secondaryText }}>{isOpen ? '▲' : '▼'}</span>
                          </div>
                        </button>

                        {/* ③ Idea #3 — Condition cards */}
                        {isOpen && (
                          <div className="p-2 space-y-1.5"
                            style={{ background: `linear-gradient(135deg, ${colors.accent}04 0%, ${colors.accent}01 100%)` }}>
                            {visible.map(cond => {
                              const selCond = formData.conditions.find(c => c.name === cond.name);
                              const selected = !!selCond;
                              const dirColor = cond.direction === 'bullish' ? colors.profit
                                : cond.direction === 'bearish' ? colors.loss : colors.accent;
                              const dirIcon = cond.direction === 'bullish' ? '▲' : cond.direction === 'bearish' ? '▼' : '◆';
                              return (
                                <button key={cond.name} onClick={() => toggleCondition(cond)}
                                  className="w-full text-left rounded-xl p-2.5 transition-all"
                                  style={{
                                    background: selected
                                      ? `linear-gradient(135deg, ${dirColor}22 0%, ${dirColor}08 100%)`
                                      : `linear-gradient(135deg, ${colors.cardBg} 0%, ${colors.cardBg} 100%)`,
                                    border: `1px solid ${selected ? dirColor + '55' : colors.border}`,
                                    boxShadow: selected ? `0 0 0 1px ${dirColor}20` : 'none',
                                  }}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-start gap-2 min-w-0">
                                      <span className="text-[11px] mt-0.5 flex-shrink-0 font-bold" style={{ color: dirColor }}>{dirIcon}</span>
                                      <div className="min-w-0">
                                        <p className="text-[13px] font-semibold leading-tight"
                                          style={{ color: selected ? dirColor : colors.primaryText }}>
                                          {cond.display}
                                        </p>
                                        <p className="text-[11px] mt-0.5 leading-snug" style={{ color: colors.secondaryText }}>
                                          {cond.desc}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                                        style={{
                                          background: cond.source === 'scanner'
                                            ? `linear-gradient(135deg, #f59e0b25 0%, #f59e0b10 100%)`
                                            : `linear-gradient(135deg, ${colors.accent}25 0%, ${colors.accent}10 100%)`,
                                          color: cond.source === 'scanner' ? '#f59e0b' : colors.accent,
                                        }}>
                                        {cond.source === 'scanner' ? 'SCANNER' : 'LIVE'}
                                      </span>
                                      {selected && (
                                        <span className="text-[12px] font-bold" style={{ color: dirColor }}>✓</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Inline params when selected */}
                                  {selected && (cond.params || []).length > 0 && (
                                    <div className="mt-2 pt-2 border-t" style={{ borderColor: `${dirColor}25` }}
                                      onClick={e => e.stopPropagation()}>
                                      {cond.params.map(p => {
                                        const val = selCond.params?.[p.key] ?? p.default;
                                        return (
                                          <div key={p.key} className="flex items-center gap-2">
                                            <span className="text-[11px] whitespace-nowrap" style={{ color: colors.secondaryText }}>{p.label}</span>
                                            <input type="range" min={p.min} max={p.max} step={p.step}
                                              value={val}
                                              onChange={e => updateConditionParam(cond.name, p.key,
                                                p.step >= 1 ? parseInt(e.target.value) : parseFloat(e.target.value)
                                              )}
                                              className="flex-1" />
                                            <span className="text-[12px] font-bold w-10 text-right" style={{ color: dirColor }}>
                                              {val}{p.unit}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="mt-2 text-[11px]" style={{ color: colors.secondaryText }}>
                  LIVE = evaluates from live market data · SCANNER = uses last scanner run (24h)
                </p>
              </div>

              {/* ── ACTIONS ── */}
              <div className="flex gap-3 pt-3 border-t" style={{ borderColor: colors.border }}>
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="flex-1 py-3 rounded-xl text-[14px] font-semibold transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${colors.accent}10 0%, ${colors.accent}04 100%)`,
                    border: `1px solid ${colors.accent}25`,
                    color: colors.secondaryText,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={editingStrategy ? handleUpdateStrategy : handleCreateStrategy}
                  className="py-3 rounded-xl text-[14px] font-bold text-white transition-all"
                  style={{
                    flex: 2,
                    background: `linear-gradient(135deg, ${colors.accent} 0%, #8b5cf6 100%)`,
                    boxShadow: `0 4px 20px ${colors.accent}50`,
                  }}
                  data-testid="save-strategy-btn"
                >
                  {editingStrategy ? 'Update Strategy' : 'Create Strategy'}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlgoFlowsPage;
